import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchBrands, createBrand, updateBrand, deleteBrand } from "../api/BrandAPIs";
import { colors } from "../constants/colors";
import {
  ChevronRight,
  Package,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Image as ImageIcon,
  Layers,
  Eye,
  Building2,
  AlertCircle,
  Tag,
  ToggleLeft
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const BrandsPage = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);

  const { data: brandData, isLoading: isLoadingBrands, error: errorBrands } = useQuery({
    queryKey: ["brands"],
    queryFn: fetchBrands,
    staleTime: Infinity,
  });

  const isLoading = isLoadingBrands;
  const error = errorBrands;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (brandData) {
      setBrands(
        brandData.map((brand) => ({
          id: brand.brandId,
          name: brand.name,
          imageBase64: brand.imageBase64 || "",
        }))
      );
    }
  }, [brandData]);

  const handleImageChange = (e, setState, state) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setState({
          ...state,
          imageBase64: reader.result.split(",")[1],
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e, setState, state) => {
    const { name, value } = e.target;
    setState({
      ...state,
      [name]: value,
    });
  };

  const handleCreateBrand = async (e) => {
    e.preventDefault();
    try {
      const created = await createBrand(newBrand);
      setBrands([
        ...brands,
        {
          id: created.brandId,
          name: created.name,
          imageBase64: created.imageBase64,
        },
      ]);
      setNewBrand(null);
    } catch (err) {
      console.error("Error creating brand:", err);
    }
  };

  const handleUpdateBrand = async (e) => {
    e.preventDefault();
    try {
      await updateBrand(editingBrand.id, editingBrand);
      const updatedBrands = await fetchBrands();
      setBrands(
        updatedBrands.map((brand) => ({
          id: brand.brandId,
          name: brand.name,
          imageBase64: brand.imageBase64 || "",
        }))
      );
      setEditingBrand(null);
    } catch (err) {
      console.error("Error updating brand:", err);
    }
  };

  const handleDeleteBrand = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذه العلامة التجارية؟")) {
      try {
        await deleteBrand(id);
        setBrands(brands.filter((brand) => brand.id !== id));
      } catch (err) {
        console.error("Error deleting brand:", err);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Building2 className="text-teal-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">
              إدارة العلامات التجارية
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <button
              onClick={() => navigate("/types/bulk-availability")}
              className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-3 rounded-xl shadow-md hover:bg-indigo-700 hover:shadow-xl transition-all duration-300"
            >
              <ToggleLeft size={20} />
              تحديث توافر الأنواع
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
            >
              <ChevronRight size={20} />
              العودة للرئيسية
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={24} />
            <p>فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.</p>
          </div>
        )}

        {/* Stats Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-teal-100 rounded-xl">
                <Tag className="text-teal-600" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">عدد العلامات التجارية</h2>
                <p className="text-sm text-gray-600">إجمالي العلامات المتاحة</p>
              </div>
            </div>
            <div className="text-4xl font-bold text-teal-600">{brands.length}</div>
          </div>
        </div>

        {/* Add Brand Button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={() => setNewBrand({ name: "", imageBase64: "" })}
            className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 font-bold"
          >
            <Plus size={20} />
            إضافة علامة تجارية جديدة
          </button>
        </div>

        {/* Brands List */}
        {isLoading ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6">
            <SkeletonLoader type="table" />
          </div>
        ) : brands.length ? (
          <div className="space-y-4">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all"
              >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex items-center gap-4">
                    {brand.imageBase64 ? (
                      <img
                        src={`data:image/png;base64,${brand.imageBase64}`}
                        alt={brand.name}
                        className="w-20 h-20 object-cover rounded-xl border-2 border-gray-200 shadow-sm"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                        <ImageIcon className="text-gray-400" size={32} />
                      </div>
                    )}
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">{brand.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">رقم: #{brand.id}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full md:w-auto">
                    <button
                      onClick={() => navigate(`/brands/${brand.id}/types`)}
                      className="flex-1 md:flex-none bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                    >
                      <Eye size={16} />
                      عرض الأنواع
                    </button>
                    <button
                      onClick={() =>
                        setEditingBrand({
                          id: brand.id,
                          name: brand.name,
                          imageBase64: brand.imageBase64,
                        })
                      }
                      className="flex-1 md:flex-none bg-amber-500 text-white px-4 py-2 rounded-xl hover:bg-amber-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                    >
                      <Edit size={16} />
                      تعديل
                    </button>
                    <button
                      onClick={() => handleDeleteBrand(brand.id)}
                      className="flex-1 md:flex-none bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                    >
                      <Trash2 size={16} />
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-16 text-center">
            <Building2 className="mx-auto mb-4 text-gray-400" size={64} />
            <p className="text-2xl text-gray-700 mb-2">لا توجد علامات تجارية</p>
            <p className="text-lg text-gray-500">قم بإضافة علامة تجارية جديدة للبدء</p>
          </div>
        )}

        {/* Create Modal */}
        {newBrand && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">إضافة علامة تجارية جديدة</h2>
                <button
                  onClick={() => setNewBrand(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleCreateBrand} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم العلامة التجارية
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newBrand.name}
                      onChange={(e) => handleInputChange(e, setNewBrand, newBrand)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-teal-300 focus:border-teal-400 transition-all"
                      placeholder="أدخل اسم العلامة التجارية"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      صورة العلامة التجارية
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-teal-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setNewBrand, newBrand)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setNewBrand(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    إنشاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingBrand && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
                <h2 className="text-2xl font-bold">تعديل العلامة التجارية #{editingBrand.id}</h2>
                <button
                  onClick={() => setEditingBrand(null)}
                  className="hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleUpdateBrand} className="p-6">
                <div className="space-y-4">
                  {editingBrand.imageBase64 && (
                    <div>
                      <label className="block text-right font-medium text-gray-700 mb-2">
                        الصورة الحالية
                      </label>
                      <img
                        src={`data:image/png;base64,${editingBrand.imageBase64}`}
                        alt="Current Brand"
                        className="h-32 w-32 object-cover rounded-xl border-2 border-gray-200 mx-auto"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      اسم العلامة التجارية
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editingBrand.name}
                      onChange={(e) => handleInputChange(e, setEditingBrand, editingBrand)}
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
                      placeholder="أدخل اسم العلامة التجارية"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-right font-medium text-gray-700 mb-2">
                      صورة جديدة (اختياري)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-amber-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, setEditingBrand, editingBrand)}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-4 mt-8">
                  <button
                    type="button"
                    onClick={() => setEditingBrand(null)}
                    className="flex items-center gap-2 bg-gray-400 text-white px-6 py-3 rounded-xl hover:bg-gray-500 font-bold text-lg transition"
                  >
                    <X size={20} />
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg"
                  >
                    <Save size={20} />
                    حفظ التغييرات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrandsPage;